import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getRetestTraceability,
  getRetestTraceabilityExport,
} from "../../../../actions/labOpsAnalytics";
import useAbortController from "../../../Common/hooks/useAbortController";
import { formatAntdDateRange } from "../../../../utils/helpers";
import { getDefaultLastOneMonthDateRange, retestFilterCatalogs, retestFilterFields } from "../utils/utils";
import {
  buildRetestDrillDownExportParams,
  buildRetestExportFileName,
  buildRetestExportRows,
  buildRetestRowExportParams,
  buildRetestCustomerLookup,
  buildRetestTraceabilityParams,
  DEFAULT_RETEST_AGGREGATE,
  downloadRetestExportExcel,
  filterRetestRowsForDrillDown,
  normalizeRetestTraceabilityRows,
  RETEST_DRILL_DOWN_KIND,
} from "../utils/retestUtils";
import { buildCategoryLabelById, OTHER_CHART_SLICE_KEY } from "../utils/overdueUtils";
import { buildRetestRowHeaderItems } from "../DataOpsSampleModalHeader";
import DataOpsFilters from "../DataOpsFilters";
import RetestTable from "../tables/RetestTable";
import RetestCharts from "../charts/RetestCharts";
import RetestDrillDownModal from "../RetestDrillDownModal";
import "../DataOpsAnalytics.css";

const createDefaultRetestFilters = () => ({
  dateRange: getDefaultLastOneMonthDateRange(),
  testCategoryId: [],
  testId: [],
  customerId: "",
  search: "",
  createdBy: "",
  subscriptionOnly: false,
});

/**
 * Retest tab — filters + charts + table + Excel export + API-scoped drill-down.
 */
export default function RetestTab() {
  const isMounted = useRef(true);
  const {
    setNewAbortController,
    clearAbortController,
  } = useAbortController();
  const {
    setNewAbortController: setDrillDownAbortController,
    clearAbortController: clearDrillDownAbortController,
  } = useAbortController();
  const {
    setNewAbortController: setExportAbortController,
    clearAbortController: clearExportAbortController,
  } = useAbortController();

  const [filters, setFilters] = useState(createDefaultRetestFilters);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const [reasonAggregate, setReasonAggregate] = useState(DEFAULT_RETEST_AGGREGATE);

  const categoryLabelById = buildCategoryLabelById(categoryOptions);
  const customerLookup = buildRetestCustomerLookup(customerOptions);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setDrillDown(null);
    clearDrillDownAbortController();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchRetestRows = async () => {
      const params = buildRetestTraceabilityParams(filters, formatAntdDateRange);
      if (!params) {
        setRows([]);
        return;
      }

      setLoading(true);
      const abortController = setNewAbortController();
      const response = await getRetestTraceability(params, abortController.signal);

      if (!isMounted.current) {
        return;
      }
      if (response.canceled) {
        setLoading(false);
        clearAbortController();
        return;
      }

      setRows(response.success ? normalizeRetestTraceabilityRows(response.result) : []);
      if (!response.success) {
        toast.error(response.message || "Failed to load retest traceability.");
      }
      setLoading(false);
      clearAbortController();
    };

    fetchRetestRows();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const openExportScopedDrillDown = async ({
    title,
    subtitle,
    headerItems = null,
    params,
    selection = null,
  }) => {
    if (!params) {
      toast.warn("Unable to open this breakdown for the current selection.");
      return;
    }

    setDrillDown({
      title,
      subtitle,
      headerItems,
      rows: [],
      loading: true,
    });
    const abortController = setDrillDownAbortController();
    const response = await getRetestTraceabilityExport(params, abortController.signal);

    if (!isMounted.current) {
      return;
    }
    if (response.canceled) {
      setDrillDown(null);
      clearDrillDownAbortController();
      return;
    }
    if (!response.success) {
      setDrillDown(null);
      clearDrillDownAbortController();
      toast.error(response.message || "Failed to load retest breakdown.");
      return;
    }

    const exportRows = normalizeRetestTraceabilityRows(response.result);
    setDrillDown({
      title,
      subtitle,
      headerItems,
      rows: selection?.kind === RETEST_DRILL_DOWN_KIND.REASON_PERIOD
        ? filterRetestRowsForDrillDown(exportRows, selection)
        : exportRows,
      loading: false,
    });
    clearDrillDownAbortController();
  };

  const handleExportTableData = async () => {
    if (exportLoading) {
      return;
    }

    const params = buildRetestTraceabilityParams(filters, formatAntdDateRange);
    if (!params) {
      toast.warn("Select a valid date range before exporting.");
      return;
    }

    setExportLoading(true);
    const abortController = setExportAbortController();
    const response = await getRetestTraceabilityExport(params, abortController.signal);

    if (!isMounted.current) {
      return;
    }
    if (response.canceled) {
      setExportLoading(false);
      clearExportAbortController();
      return;
    }
    if (!response.success) {
      setExportLoading(false);
      clearExportAbortController();
      toast.error(response.message || "Failed to export retest data.");
      return;
    }

    const exportRows = buildRetestExportRows(
      normalizeRetestTraceabilityRows(response.result),
      categoryLabelById,
      customerLookup,
    );
    setExportLoading(false);
    clearExportAbortController();

    if (!exportRows.length) {
      toast.warn("No data available to export for the current filters.");
      return;
    }
    downloadRetestExportExcel(exportRows, buildRetestExportFileName());
    toast.success("Retest data exported successfully.");
  };

  const handleChartDrillDown = (selection) => {
    if (!selection) {
      return;
    }
    if (selection.seriesKey === OTHER_CHART_SLICE_KEY) {
      toast.info("Open a named series to view the breakdown.");
      return;
    }
    if (
      selection.kind === RETEST_DRILL_DOWN_KIND.CUSTOMER
      && selection.customerPk == null
    ) {
      toast.info("Unable to open this customer breakdown for the current selection.");
      return;
    }
    if (
      selection.kind === RETEST_DRILL_DOWN_KIND.CATEGORY
      && selection.testCategoryId == null
    ) {
      toast.info("Category breakdown needs test category on the list API.");
      return;
    }
    openExportScopedDrillDown({
      title: selection.title,
      subtitle: selection.subtitle,
      params: buildRetestDrillDownExportParams(filters, selection, formatAntdDateRange),
      selection,
    });
  };

  const handleViewRow = (record) => {
    if (!record) {
      return;
    }
    openExportScopedDrillDown({
      headerItems: buildRetestRowHeaderItems(record, customerLookup),
      params: buildRetestRowExportParams(filters, record, formatAntdDateRange),
    });
  };

  return (
    <div className="DataOpsAnalytics">
      <DataOpsFilters
        idPrefix="retest"
        fields={retestFilterFields}
        catalogs={retestFilterCatalogs}
        filters={filters}
        onFiltersChange={setFilters}
        onCategoryOptionsChange={setCategoryOptions}
        onCustomerOptionsChange={setCustomerOptions}
        onExport={handleExportTableData}
        exportLoading={exportLoading}
        exportDisabled={loading || rows.length === 0}
      />

      <div className="DataOpsAnalytics__StackedLayout">
        <div className="DataOpsAnalytics__ChartsPane">
          <RetestCharts
            rows={rows}
            loading={loading}
            reasonAggregate={reasonAggregate}
            onReasonAggregateChange={setReasonAggregate}
            onDrillDown={handleChartDrillDown}
            customerLookup={customerLookup}
            categoryLabelById={categoryLabelById}
          />
        </div>
        <div className="DataOpsAnalytics__TablePane DataOpsAnalytics__TablePane--wide">
          <RetestTable
            rows={rows}
            loading={loading}
            onViewRow={handleViewRow}
          />
        </div>
      </div>

      <RetestDrillDownModal
        open={Boolean(drillDown)}
        title={drillDown?.title}
        subtitle={drillDown?.subtitle}
        headerItems={drillDown?.headerItems}
        rows={drillDown?.rows}
        loading={Boolean(drillDown?.loading)}
        categoryLabelById={categoryLabelById}
        customerLookup={customerLookup}
        onClose={() => {
          clearDrillDownAbortController();
          setDrillDown(null);
        }}
      />
    </div>
  );
}
